import { Box, Grid, Button, Typography } from '@mui/material';
import { useState, startTransition } from 'react';
import clientService from 'src/clientService';
import { Site, SiteUpdate } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';
import { Satellite } from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import { ProjectWithSites } from 'src/state-management/state';

import SiteCard from './SiteCard';

const SiteHome = () => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const { selectedProject: stateSelectedProject } = state;
  const project = stateSelectedProject?.projectDetails as ProjectWithSites | undefined;
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);

  const handleAddSiteDialogOpen = () => {
    startTransition(() => {
      navigate(`/project/${project?.project_id}/add-site`);
    });
  };

  const onSiteDelete = (site: Site) => {
    setSiteToDelete(site);
    setIsConfirmDeleteDialogOpen(true);
  };

  const triggerAnalysis = (site: Site, update: SiteUpdate) => {
    if (!site || !site.site_id) return;
    clientService.api
      .updateSiteApiV1SitesSiteIdPatch(site.site_id, update)
      .then(({ data }: { data: Site }) => {
        showSnackbar({
          message: 'Analysis triggered successfully.',
          type: snackbarType.SUCCESS,
        });
        if (project) {
          dispatch({
            type: 'SET_PROJECT_DETAILS',
            payload: {
              ...project,
              sites: project.sites?.map((s) => (s.site_id === data.site_id ? data : s)),
            } as ProjectWithSites,
          });

          dispatch({
            type: 'SET_PROJECT_LIST',
            payload: state.projects.map((p: ProjectWithSites) =>
              p.project_id === project?.project_id
                ? {
                    ...p,
                    sites: p.sites?.map((s) => (s.site_id === data.site_id ? data : s)),
                  }
                : p,
            ),
          });
        }
      })
      .catch((apiError) => {
        console.log('Failed to enable analysis:', apiError);
        showSnackbar({
          message: 'Error enabling analysis.',
          type: snackbarType.ERROR,
        });
      });
  };

  const handleCancelSiteDelete = () => {
    setIsConfirmDeleteDialogOpen(false);
    setSiteToDelete(null);
  };

  const handleConfirmSiteDelete = () => {
    if (!siteToDelete || !siteToDelete.site_id) return;

    clientService.api
      .deleteSiteApiV1SitesSiteIdDelete(siteToDelete.site_id)
      .then(() => {
        showSnackbar({
          message: 'Site deleted successfully.',
          type: snackbarType.SUCCESS,
        });

        dispatch({
          type: 'SET_PROJECT_LIST',
          payload: state.projects.map((p: ProjectWithSites) =>
            p.project_id === project?.project_id
              ? {
                  ...p,
                  sites: p.sites?.filter((s) => s.site_id !== siteToDelete.site_id),
                }
              : p,
          ),
        });

        // if (project) {
        //   dispatch({
        //     type: 'SET_PROJECT_DETAILS',
        //     payload: {
        //       ...project,
        //       sites: project.sites?.filter((s) => s.site_id !== siteToDelete.site_id),
        //     } as ProjectWithSites,
        //   });
        // }
      })
      .catch((apiError) => {
        console.log('Failed to delete site:', apiError);
        showSnackbar({
          message: 'Error deleting site.' + (apiError.error?.detail || ''),
          type: snackbarType.ERROR,
        });
      })
      .finally(() => {
        setIsConfirmDeleteDialogOpen(false);
        setSiteToDelete(null);
      });
  };

  return (
    <Box>
      <Box mt={1}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button
            variant="contained"
            endIcon={<Satellite />}
            color={'secondary'}
            onClick={handleAddSiteDialogOpen}
          >
            Add New Site
          </Button>
        </Box>

        {!project?.sites || project.sites.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            flexDirection="column"
            gap={3}
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 4,
            }}
          >
            <Satellite sx={{ fontSize: 80, color: 'text.secondary' }} />
            <Box textAlign="center">
              <Typography variant="h5" component="h2" gutterBottom>
                No Sites Added Yet
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ maxWidth: 500, mb: 3 }}
              >
                Start by adding your first site to this project. Sites are specific locations within
                your project area where you'll collect data and monitor environmental impact.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<Satellite />}
                color="secondary"
                onClick={handleAddSiteDialogOpen}
              >
                Add Your First Site
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing {project.sites.length} site{project.sites.length !== 1 ? 's' : ''}
            </Typography>
            <Grid container spacing={3}>
              {project.sites.map((site: Site, index: number) => (
                <Grid item xs={12} sm={6} md={4} key={site.site_id || index}>
                  <SiteCard
                    site={site}
                    project={project}
                    triggerAnalysis={(update) => triggerAnalysis(site, update)}
                    onDelete={() => onSiteDelete(site)}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>

      {siteToDelete && (
        <ConfirmationDialog
          open={isConfirmDeleteDialogOpen}
          title="Confirm Site Deletion"
          content={`Are you sure you want to delete the site "${siteToDelete?.site_name}"? This action cannot be undone and will delete all associated data including locations, deployments, and files.`}
          confirmButtonText="Delete Site"
          cancelButtonText="Cancel"
          onConfirm={handleConfirmSiteDelete}
          onCancel={handleCancelSiteDelete}
        />
      )}
    </Box>
  );
};

export default SiteHome;
